/**
 * Small formatting helpers shared by the wallet, receive, and contacts
 * screens. Kept dependency-free so they can be imported from anywhere
 * without dragging in React or any wallet state.
 */

/**
 * Collapse a long string (typically a Zcash unified address or txid)
 * into `head…tail` form. If the string is already short enough we
 * return it unchanged so we don't introduce an ellipsis just to lose
 * a single character.
 */
export const truncateAddr = (s: string, head = 10, tail = 8): string => {
  if (s.length <= head + tail + 1) return s;
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
};

/**
 * Render a zats balance as two strings — whole and fractional — so the
 * UI can style the fractional part separately (smaller / dimmer).
 *
 * Adaptive precision: amounts ≥1 ZEC are shown with 4 decimals (enough
 * to distinguish typical transfers without flooding the row), <1 ZEC
 * gets 6 decimals to keep small balances readable.
 */
export const formatBalanceZec = (
  zats: number,
): { whole: string; fractional: string } => {
  const zec = zats / 1e8;
  const decimals = zec >= 1 ? 4 : 6;
  const formatted = zec.toFixed(decimals);
  const [whole, fractional] = formatted.split(".");
  return {
    whole: whole ?? "0",
    fractional: fractional ?? "".padEnd(decimals, "0"),
  };
};
